import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppStore } from '../../App';

// 신규 PC(미니PC/SBC) 구입 → NixOS + MO 시스템 설치 절차 가이드.
// 원본(엔지니어링 레퍼런스): 리포 루트 BOOTSTRAP.md §3(x86)·§4(rockchip)·§5(등록).
// 이 페이지는 운영자용 요약 — 서버 비밀값은 담지 않고 클라이언트 장비 설치에 한정한다.

type Block =
  | { kind: 'p'; text: string }
  | { kind: 'note'; text: string }
  | { kind: 'warn'; text: string }
  | { kind: 'steps'; items: string[] }
  | { kind: 'code'; lines: string[] };

interface Section {
  id: string;
  title: string;
  subtitle?: string;
  blocks: Block[];
}

const CodeBlock: React.FC<{ lines: string[] }> = ({ lines }) => {
  const [copied, setCopied] = useState(false);
  const text = lines.join('\n');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard 미지원 — 무시 */
    }
  };
  return (
    <div className="relative group my-3">
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 text-[11px] px-2 py-0.5 bg-gray-700 hover:bg-gray-600 text-gray-100 rounded opacity-0 group-hover:opacity-100 transition"
      >
        {copied ? '복사됨' : '복사'}
      </button>
      <pre className="bg-gray-900 text-gray-100 text-xs leading-relaxed p-3 pr-16 overflow-x-auto rounded">
        <code>{text}</code>
      </pre>
    </div>
  );
};

const BlockView: React.FC<{ block: Block }> = ({ block }) => {
  switch (block.kind) {
    case 'p':
      return <p className="text-sm text-gray-700 leading-relaxed my-2">{block.text}</p>;
    case 'note':
      return (
        <div className="text-sm text-blue-800 bg-blue-50 border-l-4 border-blue-300 px-3 py-2 my-3">
          {block.text}
        </div>
      );
    case 'warn':
      return (
        <div className="text-sm text-amber-900 bg-amber-50 border-l-4 border-amber-400 px-3 py-2 my-3">
          ⚠️ {block.text}
        </div>
      );
    case 'steps':
      return (
        <ol className="list-decimal pl-6 my-2 space-y-1 text-sm text-gray-700">
          {block.items.map((it, i) => (
            <li key={i} className="leading-relaxed">{it}</li>
          ))}
        </ol>
      );
    case 'code':
      return <CodeBlock lines={block.lines} />;
  }
};

const SECTIONS: Section[] = [
  {
    id: 'overview',
    title: '0. 개요',
    blocks: [
      {
        kind: 'p',
        text:
          '신규 PC를 사이니지 재생기(MoClient/MoPlayer 노드)로 만드는 절차다. 장비는 두 종류다: ① x86 미니PC(예: Lenovo V100Q N100), ② Rockchip SBC(ODROID M2 등 aarch64). OS는 NixOS로 재현 가능하게 관리한다.',
      },
      {
        kind: 'note',
        text:
          '핵심 개념 — 2단계 부트스트랩: (A) MoC 없이 베이스 NixOS를 clean 설치 → (B) 운영자 머신에서 release.sh로 MoC 서비스(momanager/moplayer/moclient)를 주입. MoC 서비스는 장비에 moc-packages.nix가 주입돼야만 켜진다.',
      },
      {
        kind: 'p',
        text:
          '설치가 끝나면 화면에 QR이 뜨고, 웹에 로그인한 폰으로 QR을 스캔해 원하는 모패밀리에 스크린으로 등록하면 재생이 시작된다.',
      },
    ],
  },
  {
    id: 'prep',
    title: '1. 준비물 (착수 전 1회)',
    blocks: [
      {
        kind: 'steps',
        items: [
          '운영자 머신에 MoC 리포 clone + 서브모듈 초기화(아래).',
          'Attic 바이너리 캐시 로그인(빌드 가속·전파에 필요).',
          'NixOS 25.11 minimal ISO를 USB에 기록(x86 장비용). Rockchip은 SD 이미지를 빌드하므로 ISO 불필요.',
          '신규 장비를 유선 LAN에 연결하고 운영자 머신에서 SSH 도달 가능해야 한다.',
        ],
      },
      { kind: 'code', lines: [
        'git clone git@github.com:lionhairdino/MoC.git ~/MoC && cd ~/MoC && git submodule update --init --recursive',
      ] },
      { kind: 'code', lines: [
        'attic login moc https://cache.mocombinator.com <token>',
      ] },
      {
        kind: 'note',
        text: 'release.sh 상단의 PROD_SERVER / X86_DEVICES / ODROID_HOST 가 실제 IP·호스트와 일치하는지 확인한다.',
      },
    ],
  },
  {
    id: 'x86',
    title: '2. x86 미니PC 설치',
    subtitle: '단일 generic 프로필 MoPC/x86/nixos-x86 (flake attr .#x86)을 모든 x86 장비가 공유',
    blocks: [
      { kind: 'p', text: '── 2A. 베이스 NixOS 설치 (대상 장비에 모니터·키보드를 연결하고 진행) ──' },
      { kind: 'note', text: 'NixOS를 처음 다뤄도 아래를 그대로 따라 하면 됩니다. 명령은 한 줄씩 복사해 붙여넣고 Enter. 막히면 그 단계 번호와 화면 메시지를 알려주세요.' },

      { kind: 'p', text: '① 설치 USB 만들기 (운영자 머신에서 미리 1회)' },
      { kind: 'steps', items: [
        'nixos.org/download 에서 "Minimal ISO image (64-bit Intel/AMD)" 를 내려받는다.',
        'USB 메모리(4GB 이상)를 꽂고, 그게 어느 장치인지 lsblk 로 확인한다(보통 /dev/sdb). ⚠️ 내 컴퓨터의 디스크가 아니라 방금 꽂은 USB를 골라야 한다 — 잘못 고르면 그 디스크가 지워진다.',
      ] },
      { kind: 'code', lines: [
        '# <USB장치> 를 실제 값으로 바꾼다(예: /dev/sdb). USB 내용은 전부 지워진다.',
        'sudo dd if=~/Downloads/nixos-minimal-*.iso of=<USB장치> bs=4M status=progress conv=fsync',
      ] },

      { kind: 'p', text: '② 대상 장비를 USB로 부팅' },
      { kind: 'steps', items: [
        '만든 USB를 대상 장비에 꽂고 전원을 켠다.',
        '켜지는 즉시 "부팅 메뉴" 키를 연타한다(제조사마다 다름 — 보통 F7·F11·F12·ESC·DEL 중 하나. 화면 하단에 "Boot Menu" 안내가 잠깐 뜬다).',
        '부팅 메뉴에서 USB 항목(제조사명 또는 "UEFI: ...")을 고른다. 잠시 뒤 검은 터미널 화면(NixOS 콘솔)이 뜨면, 여기서 아래 명령들을 순서대로 입력한다.',
      ] },

      { kind: 'p', text: '③ 설치할 디스크(내장 SSD) 확인' },
      { kind: 'steps', items: [
        'lsblk 로 디스크 목록을 본다. 설치 대상은 내장 SSD다(방금 꽂은 USB가 아님 — 용량으로 구분).',
        '이름 규칙: 일반/SATA SSD = /dev/sda, NVMe SSD = /dev/nvme0n1. 아래 예시는 /dev/sda 기준이다.',
      ] },
      { kind: 'code', lines: ['lsblk    # SIZE(용량)로 내장 디스크를 찾는다. 설치 USB와 헷갈리지 말 것'] },
      { kind: 'warn', text: 'NVMe SSD라면 아래에서 /dev/sda→/dev/nvme0n1, /dev/sda1→/dev/nvme0n1p1, /dev/sda2→/dev/nvme0n1p2 로 바꿔 읽는다. 파티션 작업은 고른 디스크를 전부 지우니, 디스크 이름을 반드시 다시 확인하고 진행한다.' },

      { kind: 'p', text: '④ 파티션 2개 만들기 — 부팅용(EFI 512MB) + 루트(나머지 전체)' },
      { kind: 'code', lines: [
        'sudo parted /dev/sda -- mklabel gpt',
        'sudo parted /dev/sda -- mkpart ESP fat32 1MiB 512MiB',
        'sudo parted /dev/sda -- set 1 esp on',
        'sudo parted /dev/sda -- mkpart primary 512MiB 100%',
      ] },

      { kind: 'p', text: '⑤ 파일시스템 포맷' },
      { kind: 'code', lines: [
        'sudo mkfs.fat -F 32 -n BOOT /dev/sda1     # 부팅(EFI) 파티션',
        'sudo mkfs.ext4 -L nixos /dev/sda2         # 루트(시스템) 파티션',
      ] },

      { kind: 'p', text: '⑥ 마운트 — 설치는 /mnt 아래로 들어간다 (순서 중요: 루트 먼저, 그다음 부팅)' },
      { kind: 'code', lines: [
        'sudo mount /dev/sda2 /mnt',
        'sudo mkdir -p /mnt/boot',
        'sudo mount /dev/sda1 /mnt/boot',
      ] },

      { kind: 'p', text: '⑦ 인터넷 연결 (다음 단계부터 필수 — git clone·설치가 인터넷으로 다운로드)' },
      { kind: 'steps', items: [
        '유선(가장 확실): 랜선을 꽂으면 자동 연결(DHCP). ping 으로 확인만 하면 됨.',
        '무선(WiFi): nmtui 실행 → "Activate a connection" → 내 WiFi 선택 → 비번 입력. (DHCP까지 자동)',
      ] },
      { kind: 'code', lines: [
        '# 유선이면 랜선 꽂고 바로 확인:',
        'ping -c3 nixos.org',
        '',
        '# 무선(WiFi) — 이 ISO 는 NetworkManager 라 nmtui 가 정답:',
        'nmtui',
        '#   → Activate a connection → 내 WiFi 선택 → 비번 입력 → OK',
        '#   (association + DHCP + DNS 를 nmtui 가 한 번에 처리)',
        '',
        '# 한 줄로 하려면:',
        'nmcli radio wifi on',
        'nmcli device wifi connect "와이파이이름" password "비밀번호"',
        '',
        'ping -c3 nixos.org          # 응답 오면 연결됨',
      ] },
      { kind: 'warn', text: 'wpa_cli / wpa_supplicant 를 직접 만지지 말 것. 이 ISO 의 wpa_supplicant 는 NetworkManager 가 D-Bus(-u 모드)로 몰기 때문에 파일 컨트롤 소켓이 없어 wpa_cli 가 "could not connect" 로 막힌다. WiFi 는 반드시 nmtui/nmcli 로 붙인다.' },
      { kind: 'note', text: '이름은 못 찾는데 IP 는 되면(ping 1.1.1.1 은 됨) DNS 문제. nmtui 로 안 뜨면 nmcli device wifi list 로 스캔 결과 확인.' },

      { kind: 'p', text: '⑧ 하드웨어 설정 자동 생성 + MoC 프로필 배치' },
      { kind: 'steps', items: [
        'nixos-generate-config 가 이 장비의 하드웨어 정보(hardware-configuration.nix)를 자동으로 만든다.',
        '그 파일은 건드리지 말고, MoC의 flake·configuration만 /mnt/etc/nixos 에 덮어쓴다.',
        'MoC repo 는 private 라 장비에서 clone 불가 → 부트스트랩 파일을 MOcombinator 서버에서 받는다 (GitHub 토큰 불필요).',
      ] },
      { kind: 'code', lines: [
        'sudo nixos-generate-config --root /mnt',
        '# MoC x86 부트스트랩 파일(flake·configuration·EDID 등)을 서버에서 받아 배치:',
        'curl -sL https://mocombinator.com/static/device-setup/x86-bootstrap.tar.gz | sudo tar xz -C /mnt/etc/nixos/',
      ] },
      { kind: 'note', text: 'x86-bootstrap.tar.gz 는 release.sh mocombinator 배포 시 MoPC/x86/nixos-x86/ 에서 자동 생성돼 /static 으로 서빙된다(hardware-configuration.nix 는 미포함 — 장비가 방금 생성한 자기 것을 유지). 서버가 아직 이 버전으로 배포 안 됐으면 404 → release.sh mocombinator 먼저 실행.' },

      { kind: 'p', text: '⑨ 설치 + 재부팅' },
      { kind: 'code', lines: [
        'sudo nixos-install --flake /mnt/etc/nixos#x86 --root /mnt --impure',
        'sudo reboot    # 재부팅이 시작되면 USB를 뽑는다',
      ] },
      { kind: 'note', text: '비밀번호는 설정에 이미 들어 있어 입력이 필요 없다. 이 시점엔 MoC 서비스가 아직 없어서, 그냥 리눅스가 깨끗이 부팅되면(검은 화면 또는 로그인 프롬프트) 정상이다. 이제 다음 "2B. MoC 주입"으로 넘어간다.' },
      { kind: 'note', text: '(선택·고급) 고정 IP나 특수 모니터(EDID) 설정이 필요한 경우에만 device.nix를 따로 작성한다. 대부분은 불필요하며, 안 하면 hostName="x86"·DHCP(IP 자동)로 동작한다.' },

      { kind: 'p', text: '── 2B. MoC 주입 (운영자 머신에서) ──' },
      { kind: 'steps', items: [
        '장비 LAN 연결·SSH 도달 확인: ssh yearit@<client-ip> true',
        'per-device 설정을 장비에 1회 작성(node_id는 장비마다 고유 — 배포 타겟 키).',
      ] },
      { kind: 'code', lines: [
        "ssh yearit@<client-ip> 'mkdir -p ~/moc'",
        'scp MoManager/manager.x86.json yearit@<client-ip>:~/moc/manager.json',
        'ssh yearit@<client-ip> "sed -i \'s/\\"x86-001\\"/\\"<this-node-id>\\"/\' ~/moc/manager.json"',
        "ssh yearit@<client-ip> 'echo \"{ hostName = \\\"<this-node-id>\\\"; }\" > ~/moc/device.nix'",
        '# moplayer.json seed (hwaccel=vaapi) — 없으면 첫 부팅이 SW Pixels 렌더러로 떠서',
        '#  오버레이가 구형이고 영상이 늘어난다. EGL zero-copy 렌더러로 뜨게 미리 심는다.',
        'scp MoManager/moplayer.x86.json yearit@<client-ip>:~/moc/moplayer.json',
      ] },
      {
        kind: 'warn',
        text:
          'deploy_priority: 현장 장비는 100(템플릿 기본) — 전체 롤아웃(deploy_level=100)만 수신해 안정 검증 후에만 영향받는다(권장). 카나리/테스트 장비만 0 또는 1로.',
      },
      { kind: 'steps', items: [
        'release.sh의 X86_DEVICES 배열에 "yearit@<client-ip>:<this-node-id>" 한 줄 추가.',
        '시스템 클로저 빌드/전파(→ 장비에 moc-packages.nix 주입 + flake rsync + .#x86 빌드 + Attic push + DB 등록):',
      ] },
      { kind: 'code', lines: ['./release.sh system 0'] },
      { kind: 'p', text: '장비에서 첫 switch(이후엔 시스템 릴리스를 자동 수신):' },
      { kind: 'code', lines: [
        "ssh yearit@<client-ip> 'cd ~/nixos-x86 && sudo nixos-rebuild switch --flake .#x86 --impure'",
      ] },
      {
        kind: 'note',
        text:
          'switch 후 cage 키오스크가 momanager를 기동 → momanager가 moplayer+moclient를 실행 → MoClient가 화면에 QR을 표시한다. 이후 4번(화면 등록)으로.',
      },

      { kind: 'p', text: '── 2C. 검증 ──' },
      { kind: 'code', lines: [
        'ssh yearit@<client-ip> \'systemctl status cage-tty1 --no-pager -n 5; pgrep -a -f "momanager|moplayer|moclient"\'',
        "curl https://<도메인>/moupdate/nodes | jq '.data[] | select(.node_id==\"<this-node-id>\")'",
      ] },
    ],
  },
  {
    id: 'selfinstall',
    title: '2-자가. 자가설치 (임의 x86, 운영자 SSH 0)',
    subtitle: '미니멀 NixOS 를 스스로 설치한 임의 x86 장비에서 curl 한 줄로 MoC 를 주입 — 운영자 개입 없이 장비가 스스로 빌드·자동 업데이트한다 (Track 2)',
    blocks: [
      { kind: 'p', text: '언제 쓰나: 운영자가 SSH 로 관리하는 동일 모델 장비(2A~2C, Track 1)가 아니라, 고객/사용자가 아무 x86 PC 에 미니멀 NixOS 를 직접 깐 경우. GPU·디스플레이·키오스크·QR 등록은 Track 1 스택을 그대로 재사용하므로 추가 문제 없음.' },
      { kind: 'p', text: '── ① 미니멀 NixOS 설치 (사용자) ──' },
      { kind: 'steps', items: [
        'NixOS minimal ISO 로 부팅 → 파티션·마운트 → nixos-generate-config → nixos-install → 재부팅 (일반 NixOS 설치 절차; 위 2A 의 ①~⑨ 흐름과 동일하되 MoC 부트스트랩·flake 는 건너뛴다).',
        '재부팅 후 일반 사용자 계정(uid≥1000)으로 로그인 가능하고 인터넷이 되면 준비 완료.',
      ] },
      { kind: 'p', text: '── ② curl 한 줄 (장비에서, 1회) ──' },
      { kind: 'code', lines: [
        'curl -fsSL https://mocombinator.com/static/selfinstall.sh | sudo bash',
      ] },
      { kind: 'steps', items: [
        'sudo 호출 사용자(또는 첫 uid≥1000 계정)를 자동 감지해 그 계정 홈(~/moc)에 설정을 배치한다.',
        'node_id·hostName 은 /etc/machine-id 해시로 자동 생성(장비 고유·안정).',
        '서버에서 부트스트랩 flake/configuration + 최신 컴포넌트(moc-packages.nix)를 받아 첫 nixos-rebuild switch 를 실행(수 분 — Attic/캐시에서 클로저 fetch).',
      ] },
      { kind: 'note', text: '첫 switch 는 nix.conf 에 Attic substituter·moc 키·fetch-closure 가 아직 없으므로 스크립트가 root 권한으로 --option 부트스트랩을 1회 주입해 성공시킨다. 이후 switch 는 configuration.nix 가 nix.conf 에 심어둔 값으로 동작한다.' },
      { kind: 'p', text: '── ③ 화면 QR 등록 (2A~2C 와 동일) ──' },
      { kind: 'steps', items: [
        '설치가 끝나면 화면에 QR 코드가 표시된다.',
        '모바일 MOcombinator 로그인 → "스크린 등록" → QR 촬영 → 등록 완료 → 재생 시작.',
      ] },
      { kind: 'warn', text: '자동 업데이트 모델 차이: Track 2 는 운영자 release.sh 빌드가 아니라 momanager 가 update_interval 마다 /moupdate/moc-packages 의 최신 컴포넌트를 받아 스스로 nixos-rebuild switch 한다. 현재는 "롤링 최신"(컴포넌트 카나리 게이트 없음) — 나쁜 릴리스는 자가설치 장비 전체에 반영될 수 있으나, moplayer 크래시 회귀는 momanager 의 인라인 헬스-워치가 탐지해 이전 generation 으로 자동 롤백 + skip-list 로 재적용 루프를 막는다.' },
      { kind: 'note', text: 'Track 1(운영자·동일 모델, system_mode=true)과 무관 — iota·monsterpub 등 관리형 장비는 이 경로의 영향을 받지 않는다.' },
    ],
  },
  {
    id: 'rockchip',
    title: '3. Rockchip SBC 설치 (ODROID M2 등)',
    subtitle: 'RK3588 기반 aarch64. SD 카드 또는 eMMC',
    blocks: [
      { kind: 'p', text: '── 3A. NixOS SD 이미지 빌드 (운영자 머신) ──' },
      { kind: 'code', lines: [
        'cd ~/MoC && git submodule update --init --recursive',
        'cd MoPC/rockchip/nixos-rk3588 && nix build .#sdImage-odroid-m2 --impure',
      ] },
      { kind: 'p', text: '── 3B. SD 기록 + 첫 부팅 ──' },
      {
        kind: 'warn',
        text: 'of=/dev/sdX 의 sdX를 실제 장치로 정확히 교체할 것. 잘못된 장치 선택 시 데이터가 소실된다.',
      },
      { kind: 'code', lines: [
        'sudo dd if=result/sd-image/*.img of=/dev/sdX bs=4M status=progress conv=fsync',
      ] },
      { kind: 'p', text: '보드에 꽂고 부팅. 초기 사용자 yearit(hashedPassword로 미리 설정됨).' },
      { kind: 'p', text: '── 3C. manager.json 작성 (장비에서) ──' },
      { kind: 'code', lines: [
        'ssh yearit@<client-ip>',
        'mkdir -p ~/moc && cat > ~/moc/manager.json <<EOF',
        '{',
        '  "server_url": "https://<도메인>",',
        '  "platform": "linux-aarch64",',
        '  "system_mode": true,',
        '  "deploy_priority": 0,',
        '  "node_id": "morock-001"',
        '}',
        'EOF',
      ] },
      { kind: 'p', text: '── 3D. MoManager + MPP 렌더러 초기 설치 (운영자 머신) ──' },
      { kind: 'code', lines: [
        'MM_STORE=$(nix build .#momanager-aarch64 --print-out-paths --impure)',
        'ssh yearit@<client-ip> "sudo nix profile install --profile /nix/var/nix/profiles/moc-momanager \'$MM_STORE\'"',
        'ssh yearit@<client-ip> "sudo systemctl enable --now momanager"',
      ] },
      { kind: 'code', lines: [
        'MPP_STORE=$(nix build .#mpp-renderer-aarch64 --print-out-paths --impure)',
        'ssh yearit@<client-ip> "sudo nix profile install --profile /nix/var/nix/profiles/moc-mpp-renderer \'$MPP_STORE\'"',
      ] },
      { kind: 'p', text: '── 3E. 검증 ──' },
      { kind: 'code', lines: [
        "ssh yearit@<client-ip> 'systemctl status momanager cage-tty1 --no-pager -n 5'",
      ] },
      { kind: 'p', text: 'TV 연결 후 화면에 QR이 뜨면 4번(화면 등록)으로.' },
    ],
  },
  {
    id: 'register',
    title: '4. 화면 등록 (MoClient QR)',
    blocks: [
      { kind: 'p', text: '설치가 끝난 장비 화면에 QR이 표시된다. MOcombinator에 로그인한 모바일로:' },
      { kind: 'steps', items: [
        '대시보드 → "모클라이언트" → "스크린 등록".',
        '카메라로 QR 촬영 → 등록할 모패밀리를 선택해 붙인다.',
        '이후 MoClient가 해당 패밀리의 영상을 P2P로 내려받고 MoPlayer가 재생한다.',
      ] },
      {
        kind: 'note',
        text: 'MOcombinator Dashboard "모클라이언트" 섹션에서 노드가 online으로 표시되는지 확인.',
      },
    ],
  },
  {
    id: 'device-wifi',
    title: '5. 운영 중 장비 Wi-Fi 연결 / 변경',
    subtitle: '설치가 끝난(MoC 가 도는) 장비의 WiFi 를 붙이거나 바꾸는 4가지 방법 — 설치 중 ISO WiFi 는 ⑦(nmtui) 참고',
    blocks: [
      { kind: 'p', text: '우선순위: ① WPS 자동 → ② W 키 온스크린 picker(키보드 직접) → ③ Wi-Fi USB 생성(웹) → ④ Wi-Fi 초기화(웹).' },

      { kind: 'p', text: '① WPS (자동) — 기본 경로' },
      { kind: 'steps', items: [
        '장비에 저장된 Wi-Fi 자격증명이 없으면 MoPlayer 가 자동으로 WPS PBC 를 트리거한다(화면에 "WPS WAIT" 표시).',
        '라우터의 WPS 버튼을 누르면 자동 연결된다. WPS 지원 공유기면 이게 가장 간단.',
      ] },

      { kind: 'p', text: '② W 키 — 키보드 직접 연결 (WPS 안 될 때 권장)' },
      { kind: 'steps', items: [
        '장비에 USB 키보드를 꽂고 W 키를 누르면 주변 SSID 스캔 목록(picker)이 화면에 뜬다.',
        '↑ ↓ 로 SSID 선택 → Enter → 비밀번호 타이핑 → Enter 로 연결(wpa_cli).',
        'Backspace=비번 삭제, R=재스캔, ESC=뒤로/닫기, H=수동연결 가이드 오버레이.',
      ] },
      { kind: 'warn', text: 'W 키 picker 는 EGL 렌더러(moplayer.json hwaccel=vaapi)에서만 동작한다. SW Pixels 폴백으로 떠 있으면 안 먹으니, 먼저 moplayer.json 의 hwaccel 을 확인(트러블슈팅 ⑥ 참고).' },

      { kind: 'p', text: '③ Wi-Fi USB 생성 (웹 리모컨) — WPS·키보드 다 안 될 때' },
      { kind: 'steps', items: [
        '웹 리모컨 상단 "Wi-Fi USB 생성" 버튼 → SSID / 비밀번호 / 보안방식(WPA2·WPA3·OPEN) 입력.',
        'moc_wifi.conf 파일이 다운로드되면 USB 에 담아 장비에 꽂는다 → 장비가 읽어서 연결.',
      ] },

      { kind: 'p', text: '④ Wi-Fi 초기화 (웹 리모컨) — 잘못된 SSID 회복용' },
      { kind: 'steps', items: [
        '웹 리모컨 디바이스 관리 줄의 빨간 "Wi-Fi 초기화" 버튼 → 저장된 자격증명 전부 삭제 + WPS PBC 무한 재시도.',
        '잘못된 SSID 에 붙었을 때만 사용. 이후 라우터 WPS 버튼으로 새로 연결.',
      ] },
    ],
  },
  {
    id: 'pitfalls',
    title: '6. 알려진 함정',
    blocks: [
      { kind: 'steps', items: [
        'standalone momanager systemd 유닛 / nix profile install(x86) 방식은 폐기됨 — x86은 cage 서비스가 momanager를 직접 실행하고 바이너리는 moc-packages.nix(fetchClosure)로 들어온다.',
        'x86 재설치 시 hardware-configuration.nix는 장비 고유(디스크 UUID)라 리포 것으로 덮어쓰지 말 것 — nixos-generate-config 생성본 유지.',
        'moclient_hash(~/moc/identity.json) 손실 시 QR 재등록이 필요하다. 백업 권장.',
        'NixOS vendored 서브모듈(nixos-rk3588)은 상류 업데이트 시 MoC 수정과 충돌 가능 — git submodule update를 기계적으로 돌리지 말 것.',
        '장비가 Attic substituter(cache.mocombinator.com)로 등록돼 있어야 빌드 클로저를 빠르게 받는다(NixOS configuration에 포함됨).',
      ] },
      {
        kind: 'note',
        text: '전체·상세 절차(서버/렌더러 포함, 비밀값 인벤토리 등)는 리포 루트 BOOTSTRAP.md, 일상 배포는 DEPLOY.md를 참조.',
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: '7. 트러블슈팅 (현장 검증)',
    subtitle: '실제 현장 설치에서 막혔던 지점과 복구법 — 증상을 찾아 그 아래 명령을 따라한다',
    blocks: [
      { kind: 'p', text: '증상 ①  설치 USB(NixOS ISO)에서 WiFi가 wpa_cli로 안 붙는다 — "could not connect to wpa_supplicant".' },
      { kind: 'warn', text: 'ISO는 NetworkManager가 wpa_supplicant를 D-Bus(-u) 전용으로 띄워 파일 소켓이 없다. wpa_cli를 쓰지 말고 nmtui로 붙인다. (설치된 시스템은 정상이라 나중엔 wpa_cli도 됨.)' },
      { kind: 'code', lines: [
        'nmtui',
        '#  Activate a connection → 내 WiFi 선택 → 비번 입력',
        'ping -c3 nixos.org',
      ] },

      { kind: 'p', text: '증상 ②  재부팅하니 "No Operating System Found" 또는 "Checking Media Presence" 후 부팅 실패.' },
      { kind: 'steps', items: [
        '원인 A: 설치 USB가 Legacy(CSM)로 부팅돼 부트로더 등록 실패 → BIOS에서 CSM/Legacy 끄고 Boot Mode=UEFI, Secure Boot=Disabled.',
        '원인 B: nixos-install이 수 GB 다운로드 중 네트워크가 끊겨 미완료 → root가 빈 상태(No OS). 이 경우 재설치.',
      ] },
      { kind: 'code', lines: [
        '# USB를 UEFI로 부팅(부트 메뉴 F12에서 "UEFI:" 붙은 항목 선택) 후 ISO에서:',
        'ls /sys/firmware/efi          # 목록 나오면 UEFI 정상 / "No such file"이면 아직 Legacy → BIOS 재설정',
        'lsblk -f                       # p1=vfat(ESP), p2=ext4 LABEL=nixos(root) 확인',
        'sudo mount /dev/nvme0n1p2 /mnt',
        'sudo mount /dev/nvme0n1p1 /mnt/boot',
        'ls /mnt/nix/store | head       # 비었으면 원인 B(재설치) / 차 있으면 원인 A(부트로더만)',
        '',
        '# 원인 A — root 내용 있음 → 부트로더만 재설치:',
        'sudo nixos-enter --root /mnt',
        'bootctl install',
        'exit',
        'sudo reboot',
        '',
        '# 원인 B — root 비었음 → ⑦~⑨ 재실행. nixos-install은 이어받기 되니 같은 명령 재실행.',
      ] },

      { kind: 'p', text: '증상 ③  운영자 머신에서 장비로 ssh가 멈춘다(비번 대기, 비대화형이라 hang).' },
      { kind: 'code', lines: [
        'ssh-copy-id yearit@<장비IP>     # 비번(yit2202) 한 번 입력 → 이후 키로 무비번 접속',
      ] },

      { kind: 'p', text: '증상 ④  release.sh system이 "hardware-configuration.nix does not exist"로 빌드 실패(신규 장비).' },
      { kind: 'note', text: 'release.sh가 이제 장비의 /etc/nixos/hardware-configuration.nix를 ~/nixos-x86로 자동 seed한다. 구버전이거나 수동 처리 시 아래 한 줄.' },
      { kind: 'code', lines: [
        'ssh yearit@<장비IP> "cp /etc/nixos/hardware-configuration.nix ~/nixos-x86/hardware-configuration.nix"',
      ] },

      { kind: 'p', text: '증상 ⑤  switch까지 됐는데 화면에 QR이 안 뜬다.' },
      { kind: 'code', lines: [
        'ssh yearit@<장비IP> "systemctl is-active cage-tty1.service"          # active 여야 함',
        'ssh yearit@<장비IP> "pgrep -a -f \'momanager|moplayer|moclient\'"     # 3개 다 떠야 함',
        'ssh yearit@<장비IP> "ls -la /tmp/moplayer.sock"                       # 소켓 있어야 moplayer 정상',
        'ssh yearit@<장비IP> "grep -iE \'qr|regist\' ~/moc/moclient.log | tail"  # 상태 확인',
      ] },
      { kind: 'note', text: 'moclient.log에 "MoClient is NOT registered — will show QR" + "QR code displayed on MoPlayer successfully"가 보이면 화면에 QR이 떠 있는 것 → 4번(화면 등록)으로. 디스플레이가 검으면 cage 로그(journalctl -u cage-tty1)에서 DRM 모드셋(HDMI 커넥터) 라인 확인.' },

      { kind: 'p', text: '증상 ⑥  오버레이(status/debug/info/QR)가 구형·저품질로 나오거나, 영상이 화면에 늘어난다.' },
      { kind: 'note', text: 'moplayer 는 부팅 시 config.hwaccel 로 렌더러를 고른다: vaapi 면 EGL zero-copy(신형 오버레이·종횡비 정상), 없으면 SW Pixels(구형 오버레이·늘림). moplayer.json 이 없던 신규 장비는 Pixels 로 뜬다. ②B 에서 moplayer.x86.json 을 심으면 예방된다.' },
      { kind: 'code', lines: [
        'ssh yearit@<client-ip> "grep -E \'Using .* backend|EGL|Pixels\' ~/moc/moplayer.log | tail -3"',
        '#  "FFmpeg/Pixels backend" 면 구형 → 아래로 교정:',
        'scp MoManager/moplayer.x86.json yearit@<client-ip>:~/moc/moplayer.json',
        'ssh yearit@<client-ip> "pkill -x moplayer"     # momanager 가 EGL 렌더러로 재기동',
        'ssh yearit@<client-ip> "grep -E \'EGL initialized|Vulkan renderer\' ~/moc/moplayer.log | tail -2"',
      ] },
    ],
  },
];

const DeviceSetupGuide: React.FC = () => {
  const { user } = useAppStore();
  // 관리자 전용 — 비관리자는 대시보드로.
  if (!(user?.isAdmin || user?.role === 'admin')) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-1">장비 설치 가이드</h1>
      <p className="text-sm text-gray-500 mb-6">
        신규 PC(x86 미니PC / Rockchip SBC) 구입 → NixOS + MO 시스템 설치 절차. &lt;client-ip&gt;·&lt;this-node-id&gt;·&lt;도메인&gt;은 실제 값으로 바꿔 실행.
      </p>

      {/* 목차 */}
      <nav className="flex flex-wrap gap-2 mb-6">
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
          >
            {s.title}
          </a>
        ))}
      </nav>

      {SECTIONS.map((s) => (
        <section key={s.id} id={s.id} className="mb-8 scroll-mt-4">
          <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-1">
            {s.title}
          </h2>
          {s.subtitle && <p className="text-xs text-gray-400 mb-2">{s.subtitle}</p>}
          {s.blocks.map((b, i) => (
            <BlockView key={i} block={b} />
          ))}
        </section>
      ))}
    </div>
  );
};

export default DeviceSetupGuide;
